import { useMemo, useState, type ComponentType } from 'react';

import useOrganizations from '@/hooks/useOrganizations';
import usePeople from '@/hooks/usePeople';
import usePhaseTypes from '@/hooks/usePhaseTypes';
import usePrograms from '@/hooks/usePrograms';
import useStaff from '@/hooks/useStaff';

import type { AutocompleteModel } from '../types';

import AutocompleteField, { type AutocompleteFieldProps } from './fields/AutocompleteField';

export type AutocompleteComponentProps = Omit<
  AutocompleteFieldProps,
  'options' | 'onSearch' | 'loading'
>;

export type AutocompleteRegistry = Record<
  AutocompleteModel,
  ComponentType<AutocompleteComponentProps>
>;

const StaffAutocomplete = (props: AutocompleteComponentProps) => {
  const { data, isPending } = useStaff();

  const options = useMemo(
    () =>
      data?.data?.map((staff) => ({
        value: staff.id,
        label: [staff.first_name, staff.last_name].filter(Boolean).join(' ') || staff.id
      })) ?? [],
    [data?.data]
  );

  return <AutocompleteField {...props} options={options} loading={isPending} />;
};

const PersonAutocomplete = (props: AutocompleteComponentProps) => {
  const [search, setSearch] = useState('');
  const { data, isPending } = usePeople({ params: { page: '1', search } });

  const options = useMemo(
    () =>
      data?.data?.results?.map((person) => ({
        value: person.id,
        label: [person.first_name, person.last_name].filter(Boolean).join(' ') || person.id
      })) ?? [],
    [data?.data?.results]
  );

  return (
    <AutocompleteField {...props} options={options} onSearch={setSearch} loading={isPending} />
  );
};

const OrganizationAutocomplete = (props: AutocompleteComponentProps) => {
  const [search, setSearch] = useState('');
  const { data, isPending } = useOrganizations({ params: { page: '1', search } });

  const options = useMemo(
    () =>
      data?.data?.results?.map((organization) => ({
        value: organization.id,
        label: organization.name || organization.id
      })) ?? [],
    [data?.data?.results]
  );

  return (
    <AutocompleteField {...props} options={options} onSearch={setSearch} loading={isPending} />
  );
};

// Programs and phase types are small, unpaginated lists, so the whole set is
// handed to the Select and filtered in the browser as the user types.
const ProgramAutocomplete = (props: AutocompleteComponentProps) => {
  const { data, isPending } = usePrograms();

  const options = useMemo(
    () => data?.data?.map((program) => ({ value: program.id, label: program.name })) ?? [],
    [data?.data]
  );

  return <AutocompleteField {...props} options={options} loading={isPending} />;
};

const PhaseTypeAutocomplete = (props: AutocompleteComponentProps) => {
  const { data, isPending } = usePhaseTypes();

  const options = useMemo(
    () => data?.data?.map((phaseType) => ({ value: phaseType.id, label: phaseType.name })) ?? [],
    [data?.data]
  );

  return <AutocompleteField {...props} options={options} loading={isPending} />;
};

export const defaultAutocompleteRegistry: AutocompleteRegistry = {
  staff: StaffAutocomplete,
  person: PersonAutocomplete,
  organization: OrganizationAutocomplete,
  program: ProgramAutocomplete,
  phase_type: PhaseTypeAutocomplete
};
